import { useState, type CSSProperties, type FormEvent } from 'react';
import { HeaderContent, HEADER_TITLE_CLASS, HEADER_BADGE_CLASS } from '../components/shell/app-header';
import { searchProgramItems, type ItemHit } from '../lib/items-search';
import { Alert } from '@/components/ui/alert';

const KIND_LABEL: Record<string, string> = {
  attendu: 'Attendu', connaissance: 'Connaissance', competence: 'Compétence',
  exemple: 'Exemple', automatisme: 'Automatisme', repere: 'Repère', notion: 'Notion', objet_etude: "Objet d'étude",
};

const SUGGESTIONS = ['règle de trois', 'symétrie axiale', 'compréhension de texte', 'cycle de l\'eau', 'nombres décimaux'];

export default function AdminItemsSearch() {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<ItemHit[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [ms, setMs] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const run = async (query: string) => {
    const text = query.trim();
    if (!text || loading) return;
    setLoading(true);
    setError(null);
    try {
      const { hits: h, ms: t } = await searchProgramItems(text, 25);
      setHits(h);
      setMs(t);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setHits(null);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void run(q);
  };

  return (
    <>
      <HeaderContent width="md" showBurger backTo="/admin" backLabel="Admin">
        <div className="flex items-center gap-2.5 min-w-0">
          <h1 className={HEADER_TITLE_CLASS}>Recherche dans le programme</h1>
          <span className={HEADER_BADGE_CLASS}>Sémantique</span>
        </div>
      </HeaderContent>
      <main className="max-w-[820px] mx-auto px-4 pb-12 pt-4">

        <form onSubmit={onSubmit}>
          <div className="flex items-center gap-2 bg-card border border-border rounded-full px-4 pl-4 pr-2 py-[6px] shadow-card">
            <span className="text-[18px] opacity-60" aria-hidden>🔍</span>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Chercher une notion, un attendu, une compétence…"
              autoFocus
              className="flex-1 min-w-0 border-none outline-none bg-transparent font-[inherit] text-[16px] text-foreground"
            />
            {q && (
              <button type="button" onClick={() => { setQ(''); setHits(null); }} aria-label="Effacer"
                className="bg-transparent border-none cursor-pointer text-[18px] text-muted-foreground">×</button>
            )}
            <button type="submit" disabled={loading || !q.trim()}
              className="shrink-0 px-[18px] py-[10px] rounded-full border-none bg-naia-blue text-card font-extrabold text-sm cursor-pointer font-[inherit] disabled:bg-naia-info-bg disabled:text-muted-foreground disabled:cursor-not-allowed">
              {loading ? '…' : 'Chercher'}
            </button>
          </div>
        </form>

        {!hits && !loading && (
          <div className="flex flex-wrap gap-2 mt-[14px]">
            {SUGGESTIONS.map((s) => (
              <button key={s} type="button" onClick={() => { setQ(s); void run(s); }}
                className="text-[13px] font-semibold text-foreground bg-card border border-border rounded-full px-3 py-[6px] cursor-pointer font-[inherit]">
                {s}
              </button>
            ))}
          </div>
        )}

        {error && <Alert>{error}</Alert>}

        {hits && (
          <p className="mt-4 text-[12px] text-muted-foreground font-bold">
            {hits.length} résultat{hits.length > 1 ? 's' : ''} · {ms} ms
          </p>
        )}

        <div className="flex flex-col gap-[10px] mt-2">
          {hits?.map((h) => (
            <div key={h.id} className="bg-card border border-border rounded-[14px] px-[14px] py-3 shadow-card">
              <div className="flex flex-wrap gap-[6px] mb-[6px]">
                <Chip>{KIND_LABEL[h.kind] ?? h.kind}</Chip>
                {h.subject && <Chip accent="var(--naia-green-bg)">{h.subject}</Chip>}
                {h.cycle && <Chip accent="var(--naia-info-bg)">{h.cycle}</Chip>}
              </div>
              <div className="text-[15px] leading-[1.45] text-foreground">{h.content}</div>
              {h.document_title && (
                <div className="mt-2 text-[11.5px] text-muted-foreground">
                  📄 {h.document_title}{h.pages && h.pages.length ? ` · p. ${h.pages.join(', ')}` : ''}
                </div>
              )}
            </div>
          ))}
          {hits && hits.length === 0 && (
            <p className="text-muted-foreground text-sm px-3">Aucun item trouvé. Essaie une autre formulation.</p>
          )}
        </div>
      </main>
    </>
  );
}

function Chip({ children, accent }: { children: React.ReactNode; accent?: string }) {
  const accentStyle = accent ? ({ '--chip-accent': accent } as CSSProperties) : undefined;
  return (
    <span
      className="text-[11px] font-extrabold bg-[var(--chip-accent,var(--naia-card-alt))] text-foreground border border-border rounded-full px-[9px] py-[2px]"
      style={accentStyle}
    >
      {children}
    </span>
  );
}
